import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle, 
  Phone, 
  FileText,
  MapPin,
  Star,
  Users,
  MessageSquare,
  HelpCircle,
  Home as HomeIcon,
  ArrowRight,
  AlertCircle,
  Link as LinkIcon,
  BookOpen
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LawnCareWebsiteContent = () => {
  const faqs = [
    {
      question: "How much should I charge for lawn mowing?",
      answer: "Pricing varies by region, yard size, and service frequency. Most lawn care pros charge $30–$80 per mow for residential lawns. On your website, you can list 'starting at' pricing or offer a quote form instead of exact prices."
    },
    {
      question: "How often should lawns be mowed?",
      answer: "During peak season, weekly mowing is typical. In spring and fall, bi-weekly may work. Your FAQ page is the perfect place to answer this for customers in your area."
    },
    {
      question: "What's included in a lawn care service?",
      answer: "Most basic lawn care includes mowing, edging, and blowing. Additional services like fertilization, aeration, and weed control are usually priced separately. Be specific about what's included on each service page."
    },
    {
      question: "Do you offer one-time or recurring service?",
      answer: "Many lawn care companies offer both. On your website, clarify your preference—whether you prioritize recurring clients or accept one-time jobs."
    },
    {
      question: "What areas do you serve?",
      answer: "List your service area clearly. Create dedicated city/neighborhood pages so people searching 'lawn care in [city]' find you."
    },
    {
      question: "How do I get a quote?",
      answer: "Explain your quote process: Do you provide instant estimates? Phone quotes? On-site visits? Make the next step obvious on every page."
    },
    {
      question: "Are you licensed and insured?",
      answer: "Yes—and say so on your site. Mention your license (if applicable in your state) and liability insurance on your About page and footer."
    },
    {
      question: "Do you provide your own equipment?",
      answer: "Yes, professionals bring their own equipment. This is a common homeowner question—answer it in your FAQ."
    },
    {
      question: "What's your cancellation policy?",
      answer: "State your policy clearly. Most pros require 24–48 hours notice. Put this on your Contact or FAQ page."
    },
    {
      question: "Can I schedule service online?",
      answer: "If you offer online scheduling, link to it prominently. If you prefer calls/texts, make that clear with a click-to-call button."
    }
  ];

  const contentMistakes = [
    {
      problem: "Thin pages with 50–100 words",
      fix: "Each page needs 300–500+ words of useful content. Google rewards depth."
    },
    {
      problem: "No photos of your actual work",
      fix: "Use real before/after photos. Stock images hurt trust and don't rank."
    },
    {
      problem: "No city or area mentioned",
      fix: "Mention your service area on every page, not just the homepage."
    },
    {
      problem: "All services crammed on one page",
      fix: "Give each service its own page with unique content and photos."
    },
    {
      problem: "10-field quote forms that nobody fills out",
      fix: "Use a 4-field form: name, phone, city, service. That's it."
    },
    {
      problem: "No CTA above the fold",
      fix: "Your phone number and quote button should be visible without scrolling."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-content/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Done-for-you websites for lawn care and landscaping companies that drive calls, quotes, and recurring clients."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-content/#webpage",
    "name": "Lawn Care Website Content Guide | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/lawn-care-website-content/",
    "description": "Exactly what to write on each lawn care website page: homepage, services, service areas, reviews, about, and contact—plus copy formulas and examples.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  const lastUpdated = "December 2024";

  return (
    <>
      <SEO 
        title="Lawn Care Website Content Guide"
        description="Exactly what to write on each lawn care website page: homepage, services, service areas, reviews, about, and contact—plus copy formulas and examples."
        keywords="lawn care website content, what to put on a lawn care website, lawn care website copy, lawn mowing service page content, lawn care website homepage content"
        canonical="https://www.leafrushmarketing.com/lawn-care-website-content/"
        ogUrl="https://www.leafrushmarketing.com/lawn-care-website-content/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Content Guide
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Lawn Care Website Content: What to Put on Each Page
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Stop guessing what to write. Here's exactly what content each page of your lawn care website needs to rank and convert.
              </p>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </a>
                <NavLink to="/websites">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    See Website Subscription Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Answer Box */}
        <section className="pb-12">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <Card className="bg-primary/5 border-primary/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    Quick Answer: 5 Must-Have Content Elements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Headline + CTA above the fold</strong> — service + city + phone number visible immediately</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Dedicated service pages</strong> — one page per service, not everything on one page</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Service area pages</strong> — city-specific pages for local SEO</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Social proof</strong> — reviews, testimonials, before/after photos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Short quote form</strong> — 4 fields max: name, phone, city, service</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* 7 Must-Have Pages Overview */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
                The 7 Must-Have Pages for Your Lawn Care Website
              </h2>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Every lawn care website needs these pages to rank locally and convert visitors.
              </p>
              
              <div className="grid gap-4">
                {[
                  { icon: HomeIcon, title: "Home", purpose: "First impression + CTA. Service + city headline, trust signals, quick links to services." },
                  { icon: FileText, title: "Services (or individual service pages)", purpose: "Explain what you do, what's included, and why you're the right choice." },
                  { icon: MapPin, title: "Service Areas", purpose: "City-specific pages that help you rank for 'lawn care in [city]' searches." },
                  { icon: Star, title: "Reviews / Testimonials", purpose: "Build trust with Google reviews and customer testimonials." },
                  { icon: Users, title: "About", purpose: "Tell your story, show your team, explain how you work." },
                  { icon: MessageSquare, title: "Contact", purpose: "Phone, form, service area, hours. Make it easy to reach you." },
                  { icon: HelpCircle, title: "FAQ", purpose: "Answer common questions about pricing, scheduling, and services." }
                ].map((page, index) => (
                  <div key={index} className="flex items-start gap-4 bg-background rounded-lg p-4 border">
                    <page.icon className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold">{page.title}</h3>
                      <p className="text-sm text-muted-foreground">{page.purpose}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Page-by-Page Content Templates */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-4">
                Page-by-Page Content Templates
              </h2>
              <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                Exactly what to write on each page of your lawn care website, with copy formulas and examples you can adapt.
              </p>

              {/* Homepage Content */}
              <div className="mb-12" id="homepage-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <HomeIcon className="h-6 w-6 text-primary" />
                      1. Homepage Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Make a strong first impression, establish credibility, and drive visitors to call or submit a quote request.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-2">Above the fold checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Headline with service + city (e.g., "Lawn Care Services in Austin, TX")</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Subhead with outcome or timeframe (e.g., "Get a free quote in 24 hours")</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Click-to-call phone number</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Quote form or "Get a Quote" button</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Trust signals (licensed, insured, years in business)</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Headline formulas you can use:</h4>
                      <div className="bg-accent/30 rounded-lg p-4 space-y-3">
                        <div>
                          <p className="font-medium text-sm text-primary">Service + City:</p>
                          <p className="text-muted-foreground">"Professional Lawn Care in [City], [State]"</p>
                        </div>
                        <div>
                          <p className="font-medium text-sm text-primary">Outcome + Timeframe:</p>
                          <p className="text-muted-foreground">"Get a Lawn You're Proud Of — Free Quote in 24 Hours"</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">6-section homepage layout:</h4>
                      <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                        <li><strong className="text-foreground">Hero:</strong> Headline, subhead, CTA, phone number</li>
                        <li><strong className="text-foreground">Trust strip:</strong> Licensed, insured, years in business, 5-star reviews</li>
                        <li><strong className="text-foreground">Services overview:</strong> 3–6 services with links to individual pages</li>
                        <li><strong className="text-foreground">Proof:</strong> Before/after photos, testimonials, review count</li>
                        <li><strong className="text-foreground">Process:</strong> How it works in 3 steps</li>
                        <li><strong className="text-foreground">Final CTA:</strong> "Ready to get started?" with form or call button</li>
                      </ol>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Photos to add:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Hero image: A freshly mowed lawn in your service area</li>
                        <li>• Before/after shots of your actual work</li>
                        <li>• You or your crew in action (builds trust)</li>
                        <li>• Equipment photos (optional, shows professionalism)</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link to each service page</li>
                        <li>• Link to service areas page</li>
                        <li>• Link to reviews/testimonials</li>
                        <li>• Link to contact/quote form</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Service Page Content */}
              <div className="mb-12" id="service-page-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <FileText className="h-6 w-6 text-primary" />
                      2. Service Page Content (Mowing Example)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Explain the service clearly, answer questions, and convert visitors looking for that specific service.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Above the fold checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>H1: "Lawn Mowing Services in [City]"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Short intro paragraph (2–3 sentences)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Phone number + "Get a Quote" button</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Mini-structure for service pages:</h4>
                      <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                        <li><strong className="text-foreground">What it is:</strong> 2–3 sentences explaining the service</li>
                        <li><strong className="text-foreground">What's included:</strong> Bullet list of deliverables</li>
                        <li><strong className="text-foreground">Process:</strong> How it works (schedule, show up, complete, invoice)</li>
                        <li><strong className="text-foreground">Pricing:</strong> "Starting at $X" or "Request a free quote"</li>
                        <li><strong className="text-foreground">FAQs:</strong> 3–5 questions specific to this service</li>
                        <li><strong className="text-foreground">CTA:</strong> "Ready to get your lawn mowed?" + form or call button</li>
                      </ol>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">"What's included" example (mowing):</h4>
                      <div className="bg-accent/30 rounded-lg p-4">
                        <ul className="space-y-1 text-muted-foreground">
                          <li>• Mowing at proper blade height for your grass type</li>
                          <li>• Edging along driveways, sidewalks, and beds</li>
                          <li>• String trimming around obstacles</li>
                          <li>• Blowing off hard surfaces</li>
                          <li>• Clippings mulched or bagged (your choice)</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Photos to add:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Before/after of a mowing job</li>
                        <li>• Close-up of clean edging</li>
                        <li>• Crew mowing a lawn</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link to related services (edging, fertilization)</li>
                        <li>• Link to service areas</li>
                        <li>• Link to reviews</li>
                        <li>• Link to contact page</li>
                      </ul>
                      <p className="text-sm text-muted-foreground mt-2">
                        Learn more about writing effective service pages: <NavLink to="/service-pages-that-rank" className="text-primary hover:underline">Service Pages That Rank</NavLink>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Service Area Page Content */}
              <div className="mb-12" id="service-area-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <MapPin className="h-6 w-6 text-primary" />
                      3. Service Area Page Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Rank for "[service] in [city]" searches and show locals you work in their area.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Above the fold checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>H1: "Lawn Care in [City], [State]"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Intro mentioning the city/area</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>CTA: Call or request a quote</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">How to list cities without sounding spammy:</h4>
                      <p className="text-muted-foreground mb-3">
                        Don't just list 50 cities on one page. Create individual pages for your main service areas, or group nearby areas logically.
                      </p>
                      <div className="bg-accent/30 rounded-lg p-4">
                        <p className="font-medium text-sm text-primary mb-2">Example "Areas We Serve" paragraph:</p>
                        <p className="text-muted-foreground text-sm">
                          "We provide lawn care services to homeowners throughout the greater Austin area, including Round Rock, Cedar Park, Pflugerville, and Georgetown. Whether you're in a new development or an established neighborhood, we've got you covered."
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Photos to add:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Jobs you've completed in that city</li>
                        <li>• Neighborhood or landmark references (optional)</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link to all service pages</li>
                        <li>• Link to contact/quote form</li>
                        <li>• Link to other service area pages</li>
                      </ul>
                      <p className="text-sm text-muted-foreground mt-2">
                        Learn more: <NavLink to="/service-area-pages-local-seo" className="text-primary hover:underline">Service Area Pages for Local SEO</NavLink>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Reviews Page Content */}
              <div className="mb-12" id="reviews-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Star className="h-6 w-6 text-primary" />
                      4. Reviews / Testimonials Page Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Build trust with social proof and help visitors feel confident choosing you.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Above the fold checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>H1: "Customer Reviews" or "What Our Clients Say"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Star rating summary (e.g., "4.9 stars from 50+ reviews")</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>CTA to get a quote or call</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Copy you can use:</h4>
                      <div className="bg-accent/30 rounded-lg p-4">
                        <p className="text-muted-foreground text-sm">
                          "Don't just take our word for it. Here's what homeowners in [City] are saying about our lawn care services."
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">How to format reviews:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Embed Google reviews directly if possible</li>
                        <li>• Or display as text with name, date, and star rating</li>
                        <li>• Include city/neighborhood when available</li>
                        <li>• Place review snippets on other pages too (homepage, service pages)</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link to services</li>
                        <li>• Link to contact/quote form</li>
                        <li>• Link to about page</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* About Page Content */}
              <div className="mb-12" id="about-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Users className="h-6 w-6 text-primary" />
                      5. About Page Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Humanize your business and build trust with visitors who want to know who they're hiring.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Simple framework:</h4>
                      <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                        <li><strong className="text-foreground">Who you are:</strong> Owner name, how long you've been doing this, why you started</li>
                        <li><strong className="text-foreground">How you work:</strong> Your approach to customer service, quality, reliability</li>
                        <li><strong className="text-foreground">What you're known for:</strong> Speed, attention to detail, communication, etc.</li>
                        <li><strong className="text-foreground">What to expect:</strong> How you handle scheduling, communication, and follow-up</li>
                      </ol>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Copy you can use:</h4>
                      <div className="bg-accent/30 rounded-lg p-4 space-y-3">
                        <p className="text-muted-foreground text-sm">
                          "I'm [Name], and I started [Company Name] in [Year] because I wanted to build a lawn care business homeowners could actually rely on."
                        </p>
                        <p className="text-muted-foreground text-sm">
                          "We're a local, owner-operated company serving [City] and the surrounding areas. Every job is done by trained professionals—never subcontractors."
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Photos to add:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Photo of you (the owner)</li>
                        <li>• Team photo (if applicable)</li>
                        <li>• Truck/equipment (builds legitimacy)</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link to services</li>
                        <li>• Link to reviews</li>
                        <li>• Link to contact</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Page Content */}
              <div className="mb-12" id="contact-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <MessageSquare className="h-6 w-6 text-primary" />
                      6. Contact Page Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Make it as easy as possible for visitors to reach you and request a quote.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Above the fold checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>H1: "Contact Us" or "Get a Free Quote"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Phone number (click-to-call)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Short contact form</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Recommended form fields (4 fields max):</h4>
                      <div className="bg-accent/30 rounded-lg p-4">
                        <ul className="space-y-1 text-muted-foreground">
                          <li>1. Name</li>
                          <li>2. Phone</li>
                          <li>3. City/ZIP</li>
                          <li>4. Service needed (dropdown or text)</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Response time statement examples:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• "We respond to all quote requests within 24 hours."</li>
                        <li>• "Call or text us—we usually reply within a few hours."</li>
                        <li>• "Submit the form and we'll call you back today."</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Other info to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Service area (list or link to service areas page)</li>
                        <li>• Business hours</li>
                        <li>• Email address (optional)</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* FAQ Page Content */}
              <div className="mb-12" id="faq-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <HelpCircle className="h-6 w-6 text-primary" />
                      7. FAQ Page Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Answer common questions upfront, reduce friction, and rank for question-based searches.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">10 example FAQ questions for lawn care:</h4>
                      <div className="bg-accent/30 rounded-lg p-4">
                        <ol className="space-y-2 text-muted-foreground list-decimal list-inside text-sm">
                          <li>How much does lawn mowing cost?</li>
                          <li>How often should I have my lawn mowed?</li>
                          <li>What's included in your lawn care service?</li>
                          <li>Do you offer one-time or recurring service?</li>
                          <li>What areas do you serve?</li>
                          <li>How do I get a quote?</li>
                          <li>Are you licensed and insured?</li>
                          <li>Do you bring your own equipment?</li>
                          <li>What's your cancellation policy?</li>
                          <li>Can I schedule service online?</li>
                        </ol>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link answers to relevant service pages</li>
                        <li>• Link to contact page</li>
                        <li>• Link to service areas</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Common Content Mistakes */}
        <section className="py-16 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
                Common Content Mistakes (And How to Fix Them)
              </h2>
              <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
                Avoid these mistakes that hurt your rankings and conversions.
              </p>

              <div className="grid gap-4">
                {contentMistakes.map((item, index) => (
                  <Card key={index}>
                    <CardContent className="p-4 flex flex-col md:flex-row gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-sm">Problem:</p>
                          <p className="text-muted-foreground text-sm">{item.problem}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 flex-1">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-sm">Fix:</p>
                          <p className="text-muted-foreground text-sm">{item.fix}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Internal Linking Map */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <LinkIcon className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Internal Linking Map for Your Site
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
                How your pages should connect to help visitors and search engines navigate.
              </p>

              <Card>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <h4 className="font-semibold mb-3">Page connection flow:</h4>
                    <div className="bg-accent/30 rounded-lg p-4">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="bg-background px-3 py-1 rounded-full border font-medium">Home</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border font-medium">Services</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border font-medium">Service Areas</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border font-medium">Contact</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Recommended nav structure:</h4>
                    <ul className="space-y-2 text-muted-foreground text-sm">
                      <li className="flex items-center gap-2">
                        <span className="font-medium text-foreground">Main nav:</span>
                        Home | Services (dropdown) | Service Areas | About | Reviews | Contact
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="font-medium text-foreground">Footer:</span>
                        All main pages + individual service links + privacy policy
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Every page should link to:</h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• At least one service page</li>
                      <li>• Contact or quote form</li>
                      <li>• Service areas (when relevant)</li>
                    </ul>
                  </div>

                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Related guides:</strong>{" "}
                      <NavLink to="/service-pages-that-rank" className="text-primary hover:underline">Service Pages That Rank</NavLink>
                      {" | "}
                      <NavLink to="/service-area-pages-local-seo" className="text-primary hover:underline">Service Area Pages for Local SEO</NavLink>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 bg-primary/5">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Want Us to Write and Build This for You?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We handle the content, design, and SEO setup—so you can focus on running your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </a>
                <NavLink to="/contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    Contact Us
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Calendly */}
        <CalendlyEmbed />

        {/* Related Reading */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-8">
                <BookOpen className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Related Reading
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { title: "Lawn Care Website Design", description: "See what's included in our done-for-you websites.", href: "/lawn-care-website-design" },
                  { title: "Lawn Care Website Cost", description: "Understand pricing options and what affects cost.", href: "/lawn-care-website-cost" },
                  { title: "Service Pages That Rank", description: "How to structure service pages for SEO.", href: "/service-pages-that-rank" },
                  { title: "Service Area Pages for Local SEO", description: "Rank in the cities where you work.", href: "/service-area-pages-local-seo" },
                  { title: "Website Subscription Pricing", description: "Our month-to-month website packages.", href: "/websites" },
                  { title: "Google Business Profile Optimization", description: "Get more calls from local search.", href: "/growth-add-ons/gbp" },
                  { title: "Free Resources", description: "Guides and checklists for lawn care businesses.", href: "/resources" }
                ].map((item, index) => (
                  <NavLink key={index} to={item.href}>
                    <Card className="h-full hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-1">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </CardContent>
                    </Card>
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions
              </h2>
              
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Last Updated */}
        <section className="py-8 border-t">
          <div className="container px-4 md:px-6">
            <p className="text-center text-sm text-muted-foreground">
              Last updated: {lastUpdated}
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default LawnCareWebsiteContent;
